import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CFormInput,
  CFormSelect,
  CRow,
  CCol,
  CPagination,
  CPaginationItem,
  CBadge,
  CSpinner,
} from "@coreui/react-pro";
import { getEarlyRenewalReminders } from "../../utils/backend";

// ACS Dashboard BRD (3 July 2026) — traceability for the ANSA App's "Get reminded to renew
// your insurance" submissions (the 60-day early-renewal block). Reads the backend list
// endpoint POST /admin/early-renewal-reminders/list which returns { total, page, limit, rows }.

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusColor = (s) => {
  switch (String(s || "").toLowerCase()) {
    case "active":
      return "success";
    case "stopped":
    case "cancelled":
      return "secondary";
    default:
      return "info";
  }
};

const EarlyRenewalReminders = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const totalPages = Math.max(Math.ceil(total / limit), 1);

  const fetchData = async (p = page, l = limit, s = search) => {
    setIsLoading(true);
    setError("");
    try {
      const result = await getEarlyRenewalReminders({ page: p, limit: l, search: s });
      setRows(Array.isArray(result?.rows) ? result.rows : []);
      setTotal(Number(result?.total) || 0);
      if (result?.page) setPage(Number(result.page));
      if (result?.limit) setLimit(Number(result.limit));
    } catch (e) {
      setError(e?.message || "Failed to load early renewal reminders.");
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  // initial load
  useEffect(() => {
    fetchData(1, limit, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchData(1, limit, search);
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handlePageChange = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    fetchData(p, limit, search);
  };

  const handlePerPageChange = (e) => {
    const l = parseInt(e.target.value, 10) || 25;
    setLimit(l);
    setPage(1);
    fetchData(1, l, search);
  };

  const sendsSummary = (sends) => {
    if (!Array.isArray(sends) || sends.length === 0) return <span className="text-muted">—</span>;
    const sent = sends.filter((x) => x.status === "sent" || x.status === "partial");
    const labels = sent.map((x) => `D-${x.interval_days}`).join(", ");
    return (
      <span title={labels}>
        <CBadge color="primary">{sent.length}</CBadge>
        {labels ? <span className="ms-1 small text-muted">{labels}</span> : null}
      </span>
    );
  };

  return (
    <CRow>
      <CCol xs={12}>
        <CCard className="mb-4">
          <CCardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <strong>Early Renewal Reminders</strong>
            <div style={{ minWidth: 260 }}>
              <CFormInput
                type="text"
                placeholder="Search reg no / name / email / ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </CCardHeader>
          <CCardBody>
            <div className="mb-2 text-medium-emphasis small">
              "Get reminded to renew your insurance" submissions from the ANSA App 60-day
              early-renewal block. {total} total.
            </div>

            {error ? <div className="text-danger mb-3">{error}</div> : null}

            <div className="table-responsive">
              <CTable borderless striped align="middle" className="mb-0">
                <CTableHead className="text-nowrap">
                  <CTableRow>
                    <CTableHeaderCell>#</CTableHeaderCell>
                    <CTableHeaderCell>Vehicle Reg</CTableHeaderCell>
                    <CTableHeaderCell>Owner Name</CTableHeaderCell>
                    <CTableHeaderCell>ID Number</CTableHeaderCell>
                    <CTableHeaderCell>Email</CTableHeaderCell>
                    <CTableHeaderCell>Contact</CTableHeaderCell>
                    <CTableHeaderCell>Next NCD</CTableHeaderCell>
                    <CTableHeaderCell>Policy Expiry</CTableHeaderCell>
                    <CTableHeaderCell>Earliest Renew</CTableHeaderCell>
                    <CTableHeaderCell>Channel</CTableHeaderCell>
                    <CTableHeaderCell>Status</CTableHeaderCell>
                    <CTableHeaderCell>Reminders Sent</CTableHeaderCell>
                    <CTableHeaderCell>Submitted</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {isLoading ? (
                    <CTableRow>
                      <CTableDataCell colSpan={13} className="text-center py-4">
                        <CSpinner size="sm" /> Loading…
                      </CTableDataCell>
                    </CTableRow>
                  ) : rows.length === 0 ? (
                    <CTableRow>
                      <CTableDataCell colSpan={13} className="text-center py-4 text-muted">
                        No early renewal reminders found.
                      </CTableDataCell>
                    </CTableRow>
                  ) : (
                    rows.map((r, index) => (
                      <CTableRow key={r.id}>
                        <CTableDataCell>{index + 1 + limit * (page - 1)}</CTableDataCell>
                        <CTableDataCell className="fw-semibold">{r.vehicle_reg_no || "-"}</CTableDataCell>
                        <CTableDataCell>{r.full_name || r.account_name || "-"}</CTableDataCell>
                        <CTableDataCell>{r.id_number || "-"}</CTableDataCell>
                        <CTableDataCell>{r.email || r.account_email || "-"}</CTableDataCell>
                        <CTableDataCell>{r.contact_no || "-"}</CTableDataCell>
                        <CTableDataCell>
                          {r.next_effective_ncd !== null && r.next_effective_ncd !== undefined && r.next_effective_ncd !== ""
                            ? `${r.next_effective_ncd}%`
                            : "-"}
                        </CTableDataCell>
                        <CTableDataCell>{fmtDate(r.policy_expiry_date)}</CTableDataCell>
                        <CTableDataCell>{fmtDate(r.earliest_renewal_date)}</CTableDataCell>
                        <CTableDataCell>{r.channel || "-"}</CTableDataCell>
                        <CTableDataCell>
                          <CBadge color={statusColor(r.status)}>{r.status || "active"}</CBadge>
                        </CTableDataCell>
                        <CTableDataCell>{sendsSummary(r.sends)}</CTableDataCell>
                        <CTableDataCell>{fmtDateTime(r.created_at)}</CTableDataCell>
                      </CTableRow>
                    ))
                  )}
                </CTableBody>
              </CTable>
            </div>

            <div className="d-flex justify-content-between align-items-center flex-wrap mt-3 gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted">Rows per page</span>
                <CFormSelect
                  size="sm"
                  style={{ width: 90 }}
                  value={limit}
                  onChange={handlePerPageChange}
                  options={[
                    { label: "25", value: 25 },
                    { label: "50", value: 50 },
                    { label: "100", value: 100 },
                    { label: "200", value: 200 },
                  ]}
                />
                <span className="small text-muted">
                  Page {page} of {totalPages}
                </span>
              </div>
              <CPagination align="end" className="mb-0">
                <CPaginationItem disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
                  Prev
                </CPaginationItem>
                <CPaginationItem active>{page}</CPaginationItem>
                <CPaginationItem disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
                  Next
                </CPaginationItem>
              </CPagination>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default EarlyRenewalReminders;
